import { useState, useEffect } from 'react';
import { 
  Product, 
  CreateProductFormData
} from '../types/product';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  getDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { usePermissions } from './usePermissions';

// Helper function to remove undefined values from objects (Firestore doesn't support undefined)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item)).filter(item => item !== undefined);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    });
    return cleaned;
  }
  
  return obj;
};

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { hasAccess } = usePermissions();

  // Automatikus termékkód generálás típus alapján
  const generateProductCode = (type: 'product' | 'service' | 'package' = 'product'): string => {
    // Prefix meghatározása típus alapján
    const prefix = type === 'service' ? 'YSE' : type === 'package' ? 'YPA' : 'YPR';
    
    // Adott prefixű kódok keresése a meglévő termékek között
    const allCodes = products
      .map(product => product.code)
      .filter(code => code && code.startsWith(prefix) && code.includes('-'));
    
    const codes = allCodes
      .map(code => {
        // Prefix után a számrész kinyerése (pl. YCP-00001 -> 00001)
        const parts = code.split('-');
        if (parts.length !== 2) return 0;
        const num = parseInt(parts[1], 10);
        return isNaN(num) ? 0 : num;
      })
      .filter(num => num > 0) // Csak érvényes számokat tartunk meg
      .sort((a, b) => a - b);

    // Ha vannak kódok, a legnagyobb utáni számot adjuk vissza
    let nextNumber = 1;
    if (codes.length > 0) {
      const maxNumber = Math.max(...codes);
      nextNumber = maxNumber + 1;
    }

    // 5 jegyű formátum biztosítása (00001, 00002, stb.)
    const paddedNumber = nextNumber.toString().padStart(5, '0');
    const generatedCode = `${prefix}-${paddedNumber}`;
    
    return generatedCode;
  };

  // Real-time listener
  useEffect(() => {
    // Ha nincs hozzáférés, ne töltsünk be adatokat
    if (!hasAccess('products')) {
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const loadedProducts = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          organizationProducts: doc.data().organizationProducts?.map((op: any) => ({
            ...op,
            createdAt: op.createdAt?.toDate() || new Date(),
            updatedAt: op.updatedAt?.toDate() || new Date(),
            validFrom: op.validFrom?.toDate() || new Date(),
            validUntil: op.validUntil?.toDate() || new Date()
          })) || []
        })) as Product[];
        
        setProducts(loadedProducts);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading products:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [hasAccess]);

  const createProduct = async (data: CreateProductFormData): Promise<string> => {
    try {
      const now = new Date();
      
      console.log('useProducts createProduct hívva:', {
        images: data.images?.length || 0,
        hiddenImages: data.hiddenImages?.length || 0
      });
      
      // Biztosítsuk, hogy a thumbnailUrl egyezzen a főképpel
      let thumbnailUrl = data.thumbnailUrl;
      if (data.images && data.images.length > 0) {
        // Megkeressük a főképet
        const mainImage = data.images.find(img => img.isMain);
        if (mainImage) {
          // Felülírjuk a thumbnailUrl-t a főkép URL-jével
          thumbnailUrl = mainImage.url;
        } else {
          // Ha nincs főkép, az első képet tesszük meg főképnek
          data.images[0].isMain = true;
          thumbnailUrl = data.images[0].url;
        }
      }
      
      const newProduct = {
        code: data.code,
        name: data.name,
        description: data.description,
        type: data.type,
        unit: data.unit,
        isActive: data.isActive,
        ...(data.organizationId && { organizationId: data.organizationId }),
        organizationProducts: data.organizations.map(org => ({
          ...org,
          productId: '',
          isActive: true,
          discounts: org.discounts || [],
          createdAt: now,
          updatedAt: now,
          validFrom: org.validFrom || now,
          validUntil: org.validUntil || new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
        })),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(data.category && { 
          category: {
            categoryId: data.category.categoryId,
            fullPath: data.category.fullPath,
            level1: data.category.level1,
            ...(data.category.level2 !== undefined && { level2: data.category.level2 })
          }
        }),
        ...(data.articleNumber !== undefined && { articleNumber: data.articleNumber }),
        ...(data.barcode !== undefined && { barcode: data.barcode }),
        currency: data.currency || 'HUF',
        ...(data.netPrice !== undefined && { netPrice: data.netPrice }),
        ...(data.grossPrice !== undefined && { grossPrice: data.grossPrice }),
        ...(data.webPrice !== undefined && { webPrice: data.webPrice }),
        ...(data.taxRate !== undefined && { taxRate: data.taxRate }),
        // Automatikusan kiszámítjuk a grossPrice-t, ha nincs megadva
        ...(!data.grossPrice && data.netPrice && data.taxRate && {
          grossPrice: (() => {
            const taxRateNum = typeof data.taxRate === 'string' 
              ? parseFloat(data.taxRate.replace('%', '')) 
              : data.taxRate;
            return Math.round(data.netPrice * (1 + taxRateNum / 100) * 100) / 100;
          })()
        }),
        ...(data.reverseTaxation !== undefined && { reverseTaxation: data.reverseTaxation }),
        ...(data.webStore !== undefined && { webStore: data.webStore }),
        ...(thumbnailUrl && { thumbnailUrl }),
        ...(data.images && data.images.length > 0 && { images: data.images }),
        ...(data.hiddenImages && data.hiddenImages.length > 0 && { hiddenImages: data.hiddenImages }),
        ...(data.packageDetails && { packageDetails: removeUndefined(data.packageDetails) })
      };

      console.log('Firebase-be mentendő termék:', {
        ...newProduct,
        images: newProduct.images?.length || 0,
        hiddenImages: newProduct.hiddenImages?.length || 0
      });

      const docRef = await addDoc(collection(db, 'products'), newProduct);
      return docRef.id;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  };

  const updateProduct = async (id: string, data: Partial<CreateProductFormData>): Promise<void> => {
    try {
      const docRef = doc(db, 'products', id);
      const now = new Date();
      
      console.log('useProducts updateProduct hívva:', {
        images: data.images?.length || 0,
        hiddenImages: data.hiddenImages?.length || 0
      });
      
      // Beolvassuk a jelenlegi termék adatait
      const currentDoc = await getDoc(docRef);
      const currentProduct = currentDoc.data() as Product;
      
      // Biztosítsuk, hogy a thumbnailUrl egyezzen a főképpel
      let thumbnailUrl = data.thumbnailUrl;
      if (data.images && data.images.length > 0) {
        // Megkeressük a főképet
        const mainImage = data.images.find(img => img.isMain);
        if (mainImage) {
          // Felülírjuk a thumbnailUrl-t a főkép URL-jével
          thumbnailUrl = mainImage.url;
        } else {
          // Ha nincs főkép, az első képet tesszük meg főképnek
          data.images[0].isMain = true;
          thumbnailUrl = data.images[0].url;
        }
      }
      
      const updateData = {
        ...(data.code && { code: data.code }),
        ...(data.name && { name: data.name }),
        ...(data.description && { description: data.description }),
        ...(data.type && { type: data.type }),
        ...(data.unit && { unit: data.unit }),
        ...(data.isActive !== undefined && { isActive: data.isActive }),
        ...(data.organizationId !== undefined && { organizationId: data.organizationId || null }),
        ...(data.organizations && {
          organizationProducts: data.organizations.map(org => ({
            ...org,
            productId: id,
            isActive: true,
            discounts: org.discounts || [],
            createdAt: now,
            updatedAt: now,
            validFrom: org.validFrom || now,
            validUntil: org.validUntil || new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)
          }))
        }),
        ...(data.category && { 
          category: {
            categoryId: data.category.categoryId,
            fullPath: data.category.fullPath,
            level1: data.category.level1,
            ...(data.category.level2 && { level2: data.category.level2 })
          }
        }),
        ...(data.articleNumber && { articleNumber: data.articleNumber }),
        ...(data.barcode && { barcode: data.barcode }),
        ...(data.currency && { currency: data.currency }),
        ...(data.netPrice !== undefined && { netPrice: data.netPrice }),
        ...(data.webPrice !== undefined && { webPrice: data.webPrice }),
        ...(data.taxRate && { taxRate: data.taxRate }),
        // Automatikusan kiszámítjuk a grossPrice-t termék frissítéskor
        ...((() => {
          // Ha explicit meg van adva a grossPrice, használjuk azt
          if (data.grossPrice !== undefined) {
            return { grossPrice: data.grossPrice };
          }
          
          // Használjuk az új vagy a meglévő értékeket
          const netPrice = data.netPrice !== undefined ? data.netPrice : currentProduct?.netPrice;
          const taxRate = data.taxRate !== undefined ? data.taxRate : currentProduct?.taxRate;
          
          // Ha van netPrice és taxRate (akár új, akár meglévő), számítsuk ki a grossPrice-t
          if (netPrice !== undefined && taxRate !== undefined) {
            const taxRateNum = typeof taxRate === 'string' 
              ? parseFloat(taxRate.replace('%', '')) 
              : taxRate;
            return { grossPrice: Math.round(netPrice * (1 + taxRateNum / 100) * 100) / 100 };
          }
          
          // Különben ne írjuk felül
          return {};
        })()),
        ...(data.reverseTaxation !== undefined && { reverseTaxation: data.reverseTaxation }),
        ...(data.webStore !== undefined && { webStore: data.webStore }),
        ...(data.isFeatured !== undefined && { isFeatured: data.isFeatured }),
        ...(thumbnailUrl && { thumbnailUrl }),
        ...(data.images && { images: data.images }),
        ...(data.hiddenImages && { hiddenImages: data.hiddenImages }),
        ...(data.packageDetails && { packageDetails: removeUndefined(data.packageDetails) }),
        updatedAt: serverTimestamp()
      };

      console.log('Firebase-ben frissítendő adatok:', {
        ...updateData,
        images: updateData.images?.length || 0,
        hiddenImages: updateData.hiddenImages?.length || 0
      });

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const deleteProduct = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'products', id));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  // Termékkód elérhetőségének ellenőrzése
  const checkCodeAvailability = async (code: string, excludeId?: string): Promise<{ isAvailable: boolean; existingProductName?: string }> => {
    if (!code || code.trim() === '') {
      return { isAvailable: false };
    }

    // Normalizált kód (eltávolítjuk a felesleges szóközöket)
    const normalizedCode = code.trim();
    
    // Keresés a meglévő termékek között
    const existingProduct = products.find(product => 
      product.code === normalizedCode && 
      (excludeId ? product.id !== excludeId : true)
    );

    if (existingProduct) {
      return { 
        isAvailable: false, 
        existingProductName: existingProduct.name 
      };
    }

    return { isAvailable: true };
  };

  return {
    products,
    loading,
    error,
    createProduct,
    updateProduct,
    deleteProduct,
    generateProductCode,
    checkCodeAvailability
  };
}; 